import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getProgressSteps } from '../../../store/policystreamStore';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';

export const PolicyStreamProgressBar = () => {
  const steps = useSelector(getProgressSteps);

  return (
    <Collapse in={steps.some((step) => step.status !== 'NOT_VISITED')}>
      <ProgressBarGrid>
        {steps.map((step, index) => {
          return (
            <>
              {/* Row 1: Circle and Label */}
              <StepCircle status={step.status} relevant={step.relevant}>
                {index + 1}
              </StepCircle>
              <StepLabel relevant={step.relevant}>{step.name}</StepLabel>

              {/* Row 2: Connector and Empty Cell */}
              {index !== steps.length - 1 ? (
                <StepConnector
                  complete={step.status === 'COMPLETED' || !step.relevant}
                />
              ) : (
                <Spacer />
              )}
              <Spacer />
            </>
          );
        })}
      </ProgressBarGrid>
    </Collapse>
  );
};

// Top-level container with grid
const ProgressBarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  rowGap: theme.spacing(1),
  columnGap: theme.spacing(2),
}));

// Step label
interface StepLabelProps {
  relevant: boolean;
}
const StepLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'relevant',
})<StepLabelProps>(({ theme, relevant }) => {
  return {
    fontSize: theme.typography.body2.fontSize,
    alignSelf: 'center',
    textDecoration: relevant ? 'none' : 'line-through',
  };
});

// Spacer used in empty grid cells
const Spacer = styled('div')({
  width: '100%',
  height: '100%',
});

// Circle representing step status
const StepCircle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'status' && prop !== 'relevant',
})<{ status: ProgressStepStatus; relevant: ProgressStep['relevant'] }>(
  // @ts-expect-error
  ({ status, relevant, theme }) => {
    const size = theme.spacing(6);
    const baseStyle = {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      borderRadius: '50%',
      alignSelf: 'center',
    };

    if (!relevant) {
      return {
        ...baseStyle,
        backgroundColour: theme.palette.grey[400],
        border: `${theme.spacing(0.5)} solid ${theme.palette.grey[400]}`,
      };
    }
    switch (status) {
      case 'COMPLETED':
        return {
          ...baseStyle,
          backgroundColor: theme.palette.success.main,
          border: `2px solid ${theme.palette.success.main}`,
        };
      case 'IN_PROGRESS':
        return {
          ...baseStyle,
          backgroundColor: theme.palette.background.paper,
          border: `3px solid ${theme.palette.primary.main}`,
        };
      case 'NOT_VISITED':
      default:
        return {
          ...baseStyle,
          backgroundColor: theme.palette.background.paper,
          border: `2px solid ${theme.palette.grey[400]}`,
        };
    }
  },
);

// Vertical dotted connector line
interface StepConnectorProps {
  complete: boolean;
}
const StepConnector = styled('div', {
  shouldForwardProp: (prop) => prop !== 'complete',
})<StepConnectorProps>(({ theme, complete }) => {
  const spacing = theme.spacing(1);
  return {
    width: spacing,
    height: theme.spacing(4),
    borderLeft: `${spacing} dotted ${
      complete ? theme.palette.success.main : theme.palette.grey[400]
    }`,
    justifySelf: 'center',
  };
});

export interface ProgressStep {
  name: ProgressStepName;
  status: ProgressStepStatus;
  relevant: boolean;
}

const PROGRESS_STEP_STATUSES = [
  'NOT_VISITED',
  'IN_PROGRESS',
  'COMPLETED',
] as const;
type ProgressStepStatus = typeof PROGRESS_STEP_STATUSES[number];

const PROGRESS_STEP_NAMES = [
  'Basic Information',
  'Needs Analysis',
  'Plan Selection',
  'Application Form',
] as const;
type ProgressStepName = typeof PROGRESS_STEP_NAMES[number];